import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { aplicarErrosDoServidor } from '@/api/erros';
import type { AtendenteResponse } from '@/api/generated/model';
import {
  agendarCorrecaoSchema,
  concluirChamadoSchema,
  definirPrevisaoSchema,
  transferirChamadoSchema,
  type FormularioDeConclusao,
  type FormularioDeCorrecao,
  type FormularioDePrevisao,
  type FormularioDeTransferencia,
} from '@/features/suporte/schemas/chamado.schema';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';

/**
 * Os quatro diálogos de ação do atendente, num arquivo só: são variações do
 * mesmo formulário curto (campo + observação + salvar), e separá-los em quatro
 * arquivos de 60 linhas espalharia a mesma decisão por quatro lugares.
 */

interface Base {
  aberto: boolean;
  aoFechar: () => void;
}

/**
 * `<input type="datetime-local">` devolve `2026-09-18T18:00`, sem fuso. A API
 * espera ISO com `Z`.
 *
 * ⚠️ `new Date('2026-09-18T18:00')` interpreta no fuso LOCAL (é assim que a
 * spec trata datetime sem offset), que é exatamente o que se quer aqui: o
 * atendente escolheu 18h no relógio dele.
 */
function paraIso(valorLocal: string): string {
  return new Date(valorLocal).toISOString();
}

/** ISO → o formato que o `datetime-local` aceita de volta, no fuso local. */
function paraLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const deslocado = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return deslocado.toISOString().slice(0, 16);
}

export function DefinirPrevisaoDialogo({
  aberto,
  aoFechar,
  valorAtual,
  observacaoAtual,
  aoSalvar,
  aoLimpar,
}: Base & {
  valorAtual: string | null;
  observacaoAtual: string | null;
  aoSalvar: (dados: { previsaoConclusaoEm: string; observacao?: string }) => Promise<void>;
  aoLimpar: () => Promise<void>;
}) {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormularioDePrevisao>({ resolver: zodResolver(definirPrevisaoSchema) });

  useEffect(() => {
    if (aberto) {
      reset({ previsaoConclusaoEm: paraLocal(valorAtual), observacao: observacaoAtual ?? '' });
    }
  }, [aberto, valorAtual, observacaoAtual, reset]);

  const aoEnviar = handleSubmit(async (dados) => {
    try {
      await aoSalvar({
        previsaoConclusaoEm: paraIso(dados.previsaoConclusaoEm),
        ...(dados.observacao?.trim() ? { observacao: dados.observacao.trim() } : {}),
      });
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível salvar a previsão.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Previsão de conclusão</DialogTitle>
          <DialogDescription>
            O solicitante vê esta data no chamado e recebe um aviso. Prometa o que dá para cumprir.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void aoEnviar(e)} className="space-y-4">
          {errors.root && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <Campo
            id="previsaoConclusaoEm"
            rotulo="Quando pretende concluir"
            erro={errors.previsaoConclusaoEm?.message}
            obrigatorio
          >
            <Input
              id="previsaoConclusaoEm"
              type="datetime-local"
              className={CLASSE_CONTROLE}
              {...register('previsaoConclusaoEm')}
            />
          </Campo>

          <Campo id="observacao" rotulo="Observação" erro={errors.observacao?.message}>
            <Textarea
              id="observacao"
              rows={2}
              placeholder="Ex.: depende de uma validação com o laboratório."
              className={CLASSE_CONTROLE}
              {...register('observacao')}
            />
          </Campo>

          <DialogFooter className="gap-2">
            {valorAtual && (
              <Button
                type="button"
                variant="outline"
                onClick={() => void aoLimpar().then(aoFechar)}
                className="mr-auto"
              >
                Remover previsão
              </Button>
            )}
            <Button type="button" variant="outline" onClick={aoFechar}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AgendarCorrecaoDialogo({
  aberto,
  aoFechar,
  valorAtual,
  descricaoAtual,
  aoSalvar,
  aoLimpar,
}: Base & {
  valorAtual: string | null;
  descricaoAtual: string | null;
  aoSalvar: (dados: { correcaoPrevistaEm: string; descricao?: string }) => Promise<void>;
  aoLimpar: () => Promise<void>;
}) {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormularioDeCorrecao>({ resolver: zodResolver(agendarCorrecaoSchema) });

  useEffect(() => {
    if (aberto) {
      // `valorAtual` já vem como `AAAA-MM-DD` da API — é o formato que o
      // `<input type="date">` espera, sem conversão de fuso no meio.
      reset({ correcaoPrevistaEm: valorAtual ?? '', descricao: descricaoAtual ?? '' });
    }
  }, [aberto, valorAtual, descricaoAtual, reset]);

  const aoEnviar = handleSubmit(async (dados) => {
    try {
      await aoSalvar({
        correcaoPrevistaEm: dados.correcaoPrevistaEm,
        ...(dados.descricao?.trim() ? { descricao: dados.descricao.trim() } : {}),
      });
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível agendar a correção.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Correção em produção</DialogTitle>
          <DialogDescription>
            Para quando o chamado depende de uma atualização do sistema. A data aparece para o
            solicitante — use a observação para qualificar a promessa.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void aoEnviar(e)} className="space-y-4">
          {errors.root && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <Campo
            id="correcaoPrevistaEm"
            rotulo="Data da publicação"
            erro={errors.correcaoPrevistaEm?.message}
            obrigatorio
          >
            <Input
              id="correcaoPrevistaEm"
              type="date"
              className={CLASSE_CONTROLE}
              {...register('correcaoPrevistaEm')}
            />
          </Campo>

          <Campo
            id="descricao"
            rotulo="Observação"
            erro={errors.descricao?.message}
            dica="Ex.: sujeito a validação em homologação."
          >
            <Textarea id="descricao" rows={2} className={CLASSE_CONTROLE} {...register('descricao')} />
          </Campo>

          <DialogFooter className="gap-2">
            {valorAtual && (
              <Button
                type="button"
                variant="outline"
                onClick={() => void aoLimpar().then(aoFechar)}
                className="mr-auto"
              >
                Remover data
              </Button>
            )}
            <Button type="button" variant="outline" onClick={aoFechar}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ConcluirChamadoDialogo({
  aberto,
  aoFechar,
  aoConcluir,
}: Base & { aoConcluir: (resolucao: string) => Promise<void> }) {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormularioDeConclusao>({ resolver: zodResolver(concluirChamadoSchema) });

  useEffect(() => {
    if (aberto) reset({ resolucao: '' });
  }, [aberto, reset]);

  const aoEnviar = handleSubmit(async (dados) => {
    try {
      await aoConcluir(dados.resolucao);
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível concluir o chamado.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Concluir chamado</DialogTitle>
          <DialogDescription>
            Depois disso ninguém escreve mais nesta conversa. A resolução vai por e-mail e fica
            como a última linha do chamado.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void aoEnviar(e)} className="space-y-4">
          {errors.root && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <Campo
            id="resolucao"
            rotulo="O que foi feito"
            erro={errors.resolucao?.message}
            dica="É isto que o solicitante vai ler — e o próximo atendente, se o problema voltar."
            obrigatorio
          >
            <Textarea
              id="resolucao"
              rows={4}
              placeholder="Corrigido o timeout da assinatura. Já está no ar."
              className={CLASSE_CONTROLE}
              {...register('resolucao')}
            />
          </Campo>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={aoFechar}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Concluir
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function TransferirChamadoDialogo({
  aberto,
  aoFechar,
  atendentes,
  atendenteAtualId,
  aoTransferir,
}: Base & {
  atendentes: readonly AtendenteResponse[];
  atendenteAtualId: string | null;
  aoTransferir: (dados: { atendenteId: string; motivo?: string }) => Promise<void>;
}) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormularioDeTransferencia>({
    resolver: zodResolver(transferirChamadoSchema),
    defaultValues: { atendenteId: '', motivo: '' },
  });

  useEffect(() => {
    if (aberto) reset({ atendenteId: '', motivo: '' });
  }, [aberto, reset]);

  const escolhido = watch('atendenteId');
  // Quem já é o dono não aparece — transferir para si mesmo é 409 no servidor,
  // e oferecer a opção é convidar ao erro.
  const opcoes = atendentes.filter((a) => a.id !== atendenteAtualId);

  const aoEnviar = handleSubmit(async (dados) => {
    try {
      await aoTransferir({
        atendenteId: dados.atendenteId,
        ...(dados.motivo?.trim() ? { motivo: dados.motivo.trim() } : {}),
      });
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível transferir o chamado.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Transferir chamado</DialogTitle>
          <DialogDescription>
            O histórico continua visível para quem atendeu antes. O motivo entra na conversa.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void aoEnviar(e)} className="space-y-4">
          {errors.root && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <Campo id="atendenteId" rotulo="Novo atendente" erro={errors.atendenteId?.message} obrigatorio>
            <Select value={escolhido} onValueChange={(v) => setValue('atendenteId', v)}>
              <SelectTrigger id="atendenteId" className={CLASSE_CONTROLE}>
                <SelectValue placeholder="Escolha para quem" />
              </SelectTrigger>
              <SelectContent>
                {opcoes.map((atendente) => (
                  <SelectItem key={atendente.id} value={atendente.id}>
                    {/* A carga aparece aqui para não transferir para quem já está cheio. */}
                    {atendente.nomeCompleto} · {atendente.emAndamento} em andamento
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>

          <Campo id="motivo" rotulo="Motivo" erro={errors.motivo?.message}>
            <Textarea
              id="motivo"
              rows={2}
              placeholder="Ex.: precisa de alguém do time de laboratório."
              className={CLASSE_CONTROLE}
              {...register('motivo')}
            />
          </Campo>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={aoFechar}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Transferir
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
